import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    collection, query, where, getDocs, doc, setDoc, Timestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';
import {
    BarChart, Bar, PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    LineChart, Line
} from 'recharts';
import { BarChart3, TrendingUp, TrendingDown, DollarSign, Package, Percent, AlertTriangle, CheckCircle, Info, Lightbulb, Clock } from 'lucide-react';
import PageHeader from './common/PageHeader';

const toDate = (ts) => {
    if (!ts) return null;
    if (ts.toDate) return ts.toDate();
    if (ts instanceof Date) return ts;
    return new Date(ts);
};

const formatAmount = (v) => {
    if (v >= 100000000) return `${(v / 100000000).toFixed(1)}억`;
    if (v >= 10000000) return `${(v / 10000).toFixed(0)}만`;
    if (v >= 10000) return `${Math.round(v / 10000)}만`;
    return v.toLocaleString();
};

const formatFull = (v) => Number(v).toLocaleString();

const COLORS = ['#6366f1', '#10b981', '#f59e0b', '#3b82f6', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6'];

const tabs = [
    { key: 'revenue', label: '매출 현황' },
    { key: 'cost', label: '비용 관리' },
    { key: 'profit', label: '수익성 분석' },
    { key: 'diagnosis', label: '경영 진단' },
];

function BusinessAnalysis({ user }) {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('revenue');
    const [loading, setLoading] = useState(true);
    const [revenueData, setRevenueData] = useState([]);
    const [costsData, setCostsData] = useState({});
    const [selectedMonth, setSelectedMonth] = useState('');
    const [selectedRevenueMonth, setSelectedRevenueMonth] = useState('');
    const [manualRevenueInput, setManualRevenueInput] = useState('');
    const [savingRevenue, setSavingRevenue] = useState(false);
    // 비용 입력
    const [costForm, setCostForm] = useState({
        laborCost: '', materialCost: '', rentCost: '', equipmentCost: '', otherCost: '',
        customCosts: [],
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [toast, setToast] = useState(null); // { message, type: 'success'|'error' }

    const showToast = useCallback((message, type = 'success') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 3000);
    }, []);

    const uid = user?.uid;
    const [rangeMonths, setRangeMonths] = useState(6); // 3, 6, 12

    // 조회 기간 범위
    const dateRange = useMemo(() => {
        const now = new Date();
        const end = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
        const start = new Date(now.getFullYear(), now.getMonth() - (rangeMonths - 1), 1);
        return { start, end };
    }, [rangeMonths]);

    // 현재 월 기본 설정
    useEffect(() => {
        const now = new Date();
        const cur = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
        setSelectedMonth(cur);
        setSelectedRevenueMonth(cur);
    }, []);

    // 매출 데이터 로드 (기간 변경 시 다시 로드)
    useEffect(() => {
        if (!uid) return;
        loadAllData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uid, rangeMonths]);

    const loadAllData = async () => {
        setLoading(true);
        setError(null);
        try {
            await Promise.all([loadRevenue(), loadCosts()]);
        } catch (e) {
            console.error('데이터 로드 실패:', e);
            setError('데이터를 불러오는데 실패했습니다. 다시 시도해 주세요.');
        }
        setLoading(false);
    };

    const loadRevenue = async () => {
        try {
            // 거래명세서 기반 — 매출 = 내가 발행한(fromUserId == 나) 명세서만.
            // (2026-10-13 대표 승인) 받은 명세서(toUserId == 나)는 상대가 발행한 것 = 내 매입·외주비라 매출에서 제외.
            //   예: CAD 디자인 외주 발주랩은 outsourceStatements의 toUserId(받는쪽)이므로 매출에 잡히면 안 된다
            //   (functions/lib/design-outsource.js — fromUserId=수주랩, toUserId=발주랩).
            const queries = [
                { name: 'transactionStatements', field: 'fromUserId', direction: 'sent' },
                { name: 'outsourceStatements', field: 'fromUserId', direction: 'sent' },
            ];

            const allDocs = [];
            const seen = new Set();
            for (const c of queries) {
                const q = query(
                    collection(db, c.name),
                    where(c.field, '==', uid)
                );
                const snap = await getDocs(q);
                snap.docs.forEach(d => {
                    if (!seen.has(d.id)) {
                        seen.add(d.id);
                        allDocs.push({ id: d.id, ...d.data(), direction: c.direction, source: c.name });
                    }
                });
            }

            const filtered = allDocs.filter(d => {
                // 발행처가 삭제·정정한 명세서는 매출 제외 (2026-10-23 검토 회의 — 정정본과 이중 합산 방지)
                if (d.deletedBySender) return false;
                const date = toDate(d.createdAt);
                if (!date) return false;
                return date >= dateRange.start && date <= dateRange.end;
            });

            setRevenueData(filtered);
        } catch (e) {
            console.error('매출 데이터 로드 실패:', e);
            throw e;
        }
    };

    const loadCosts = async () => {
        try {
            const q = query(
                collection(db, 'monthlyCosts'),
                where('companyId', '==', uid)
            );
            const snap = await getDocs(q);
            const map = {};
            snap.docs.forEach(d => {
                const data = d.data();
                if (data.yearMonth) {
                    map[data.yearMonth] = { id: d.id, ...data };
                }
            });
            setCostsData(map);

            const now = new Date();
            const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
            if (map[currentMonth]) {
                const c = map[currentMonth];
                setCostForm({
                    laborCost: String(c.laborCost || ''),
                    materialCost: String(c.materialCost || ''),
                    rentCost: String(c.rentCost || ''),
                    equipmentCost: String(c.equipmentCost || ''),
                    otherCost: String(c.otherCost || ''),
                    customCosts: Array.isArray(c.customCosts) ? c.customCosts.map(cc => ({ ...cc })) : [],
                });
                setManualRevenueInput(c.manualRevenue ? String(c.manualRevenue) : '');
            }
        } catch (e) {
            console.error('비용 데이터 로드 실패:', e);
            throw e;
        }
    };

    // 월 선택 시 비용 폼 업데이트
    const handleMonthChange = (month) => {
        setSelectedMonth(month);
        const c = costsData[month];
        if (c) {
            setCostForm({
                laborCost: String(c.laborCost || ''),
                materialCost: String(c.materialCost || ''),
                rentCost: String(c.rentCost || ''),
                equipmentCost: String(c.equipmentCost || ''),
                otherCost: String(c.otherCost || ''),
                customCosts: Array.isArray(c.customCosts) ? c.customCosts.map(cc => ({ ...cc })) : [],
            });
        } else {
            setCostForm({ laborCost: '', materialCost: '', rentCost: '', equipmentCost: '', otherCost: '', customCosts: [] });
        }
    };

    const handleSaveCost = async () => {
        if (!uid || !selectedMonth) return;
        setSaving(true);
        try {
            const labor = Number(costForm.laborCost) || 0;
            const material = Number(costForm.materialCost) || 0;
            const rent = Number(costForm.rentCost) || 0;
            const equipment = Number(costForm.equipmentCost) || 0;
            const other = Number(costForm.otherCost) || 0;
            const validCustomCosts = (costForm.customCosts || [])
                .filter(c => c.name && Number(c.amount) > 0)
                .map(c => ({ name: c.name, amount: Number(c.amount) }));
            const customCostTotal = validCustomCosts.reduce((s, c) => s + c.amount, 0);
            const total = labor + material + rent + equipment + other + customCostTotal;

            const docId = `${uid}_${selectedMonth}`;
            await setDoc(doc(db, 'monthlyCosts', docId), {
                companyId: uid,
                yearMonth: selectedMonth,
                laborCost: labor,
                materialCost: material,
                rentCost: rent,
                equipmentCost: equipment,
                otherCost: other,
                customCosts: validCustomCosts,
                totalCost: total,
                updatedAt: Timestamp.now(),
            }, { merge: true });

            setCostsData(prev => ({
                ...prev,
                [selectedMonth]: {
                    ...prev[selectedMonth],
                    companyId: uid, yearMonth: selectedMonth,
                    laborCost: labor, materialCost: material, rentCost: rent,
                    equipmentCost: equipment, otherCost: other, totalCost: total,
                    customCosts: validCustomCosts,
                }
            }));
            showToast('저장되었습니다.', 'success');
        } catch (e) {
            showToast('저장 실패: ' + e.message, 'error');
        }
        setSaving(false);
    };

    // 수동 매출 저장 (매출 현황 탭)
    const handleSaveManualRevenue = async () => {
        if (!uid || !selectedRevenueMonth) return;
        setSavingRevenue(true);
        try {
            const manualRevenue = Number(manualRevenueInput) || 0;
            const docId = `${uid}_${selectedRevenueMonth}`;
            await setDoc(doc(db, 'monthlyCosts', docId), {
                companyId: uid,
                yearMonth: selectedRevenueMonth,
                manualRevenue,
                updatedAt: Timestamp.now(),
            }, { merge: true });

            setCostsData(prev => ({
                ...prev,
                [selectedRevenueMonth]: {
                    ...prev[selectedRevenueMonth],
                    companyId: uid,
                    yearMonth: selectedRevenueMonth,
                    manualRevenue,
                }
            }));
            showToast('추가 매출이 저장되었습니다.', 'success');
        } catch (e) {
            showToast('저장 실패: ' + e.message, 'error');
        }
        setSavingRevenue(false);
    };

    const handleRevenueMonthChange = (month) => {
        setSelectedRevenueMonth(month);
        const c = costsData[month];
        setManualRevenueInput(c?.manualRevenue ? String(c.manualRevenue) : '');
    };

    // ─── 계산된 값 ───

    const monthlyRevenue = useMemo(() => {
        const map = {};
        revenueData.forEach(d => {
            const date = toDate(d.createdAt);
            if (!date) return;
            const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            const amount = Number(d.totalAmount || d.subtotal || 0);
            map[key] = (map[key] || 0) + amount;
        });
        // 수동 매출 합산
        Object.entries(costsData).forEach(([month, data]) => {
            const manual = Number(data.manualRevenue) || 0;
            if (manual > 0) {
                map[month] = (map[month] || 0) + manual;
            }
        });
        return map;
    }, [revenueData, costsData]);

    const partnerRevenue = useMemo(() => {
        const map = {};
        revenueData.forEach(d => {
            // 발행분만 들어오므로 거래처 = 받는 쪽(toUserName). fromUserName은 나 자신
            const name = d.toUserName || '기타';
            const amount = Number(d.totalAmount || d.subtotal || 0);
            map[name] = (map[name] || 0) + amount;
        });
        return map;
    }, [revenueData]);

    const totalRevenue = useMemo(() => Object.values(monthlyRevenue).reduce((a, b) => a + b, 0), [monthlyRevenue]);
    const totalOrderCount = revenueData.length;
    const averageUnitPrice = totalOrderCount > 0 ? totalRevenue / totalOrderCount : 0;

    const growthRateData = useMemo(() => {
        const keys = Object.keys(monthlyRevenue).sort();
        if (keys.length < 2) return { value: 0, hasData: false };
        const current = monthlyRevenue[keys[keys.length - 1]] || 0;
        const previous = monthlyRevenue[keys[keys.length - 2]] || 0;
        return previous > 0
            ? { value: ((current - previous) / previous) * 100, hasData: true }
            : { value: 0, hasData: false };
    }, [monthlyRevenue]);
    const growthRate = growthRateData.value;
    const hasGrowthData = growthRateData.hasData;

    const totalCost = useMemo(() => {
        return Object.values(costsData).reduce((sum, c) => {
            let base = (Number(c.totalCost) || 0);
            return sum + base;
        }, 0);
    }, [costsData]);

    const operatingProfit = totalRevenue - totalCost;
    const operatingProfitRate = totalRevenue > 0 ? (operatingProfit / totalRevenue) * 100 : 0;

    // 월별 수익성 차트 데이터
    const monthlyProfitData = useMemo(() => {
        const allMonths = new Set([...Object.keys(monthlyRevenue), ...Object.keys(costsData)]);
        return [...allMonths].sort().map(month => {
            const rev = monthlyRevenue[month] || 0;
            const cost = Number(costsData[month]?.totalCost) || 0;
            const profit = rev - cost;
            const rate = rev > 0 ? (profit / rev) * 100 : 0;
            const parts = month.split('-');
            return { month, label: `${Number(parts[1])}월`, revenue: rev, cost, profit, rate: Math.round(rate * 10) / 10 };
        });
    }, [monthlyRevenue, costsData]);

    // 주문/외주/수동 매출 분리 데이터
    const monthlyOrderRevenue = useMemo(() => {
        const map = {};
        revenueData.forEach(d => {
            if (d.source === 'purchaseInvoices') return;
            if (d.source !== 'outsourceStatements') {
                let date;
                if (d.issueDate && typeof d.issueDate === 'string' && d.issueDate.length >= 8) {
                    date = new Date(parseInt(d.issueDate.substring(0, 4)), parseInt(d.issueDate.substring(4, 6)) - 1, 1);
                } else {
                    date = toDate(d.createdAt);
                }
                if (!date) return;
                const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
                map[key] = (map[key] || 0) + Number(d.totalAmount || d.subtotal || 0);
            }
        });
        return map;
    }, [revenueData]);

    const monthlyOutsourceRevenue = useMemo(() => {
        const map = {};
        revenueData.forEach(d => {
            if (d.source === 'outsourceStatements') {
                const date = toDate(d.createdAt);
                if (!date) return;
                const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
                map[key] = (map[key] || 0) + Number(d.totalAmount || d.subtotal || 0);
            }
        });
        return map;
    }, [revenueData]);

    // 매입 데이터에서 재료비 자동 집계
    const monthlyPurchaseCost = useMemo(() => {
        const map = {};
        revenueData.forEach(d => {
            if (d.source === 'purchaseInvoices') {
                let date;
                if (d.issueDate && typeof d.issueDate === 'string' && d.issueDate.length >= 8) {
                    date = new Date(parseInt(d.issueDate.substring(0, 4)), parseInt(d.issueDate.substring(4, 6)) - 1, 1);
                } else {
                    date = toDate(d.createdAt);
                }
                if (!date) return;
                const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
                map[key] = (map[key] || 0) + Number(d.totalAmount || 0);
            }
        });
        return map;
    }, [revenueData]);

    const monthlyManualRevenue = useMemo(() => {
        const map = {};
        Object.entries(costsData).forEach(([month, data]) => {
            const manual = Number(data.manualRevenue) || 0;
            if (manual > 0) map[month] = manual;
        });
        return map;
    }, [costsData]);

    // 매출 추이 차트 데이터 (주문/외주/수동 구분)
    const revenueChartData = useMemo(() => {
        const allMonths = new Set([
            ...Object.keys(monthlyOrderRevenue),
            ...Object.keys(monthlyOutsourceRevenue),
            ...Object.keys(monthlyManualRevenue),
        ]);
        return [...allMonths].sort().map(month => {
            const parts = month.split('-');
            return {
                month,
                label: `${Number(parts[1])}월`,
                주문매출: monthlyOrderRevenue[month] || 0,
                외주매출: monthlyOutsourceRevenue[month] || 0,
                추가매출: monthlyManualRevenue[month] || 0,
                value: (monthlyOrderRevenue[month] || 0) + (monthlyOutsourceRevenue[month] || 0) + (monthlyManualRevenue[month] || 0),
            };
        });
    }, [monthlyOrderRevenue, monthlyOutsourceRevenue, monthlyManualRevenue]);

    // 거래처 파이 데이터
    const partnerPieData = useMemo(() => {
        const sorted = Object.entries(partnerRevenue).sort(([, a], [, b]) => b - a);
        const top5 = sorted.slice(0, 5).map(([name, value]) => ({ name, value }));
        if (sorted.length > 5) {
            const otherTotal = sorted.slice(5).reduce((sum, [, v]) => sum + v, 0);
            top5.push({ name: '기타', value: otherTotal });
        }
        return top5;
    }, [partnerRevenue]);

    // 비용 추이 차트 데이터
    const costChartData = useMemo(() => {
        return Object.entries(costsData)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([month, data]) => {
                const parts = month.split('-');
                return {
                    month, label: `${Number(parts[1])}월`,
                    인건비: Number(data.laborCost) || 0,
                    재료비: Number(data.materialCost) || 0,
                    임대료: Number(data.rentCost) || 0,
                    '장비/감가상각': Number(data.equipmentCost) || 0,
                    기타경비: Number(data.otherCost) || 0,
                    total: Number(data.totalCost) || 0,
                };
            });
    }, [costsData]);

    // 비용 항목별 파이 데이터
    const costPieData = useMemo(() => {
        let labor = 0, material = 0, rent = 0, equipment = 0, other = 0;
        const customMap = {};
        Object.values(costsData).forEach(c => {
            labor += Number(c.laborCost) || 0;
            material += Number(c.materialCost) || 0;
            rent += Number(c.rentCost) || 0;
            equipment += Number(c.equipmentCost) || 0;
            other += Number(c.otherCost) || 0;
            // 커스텀 비용 합산
            if (Array.isArray(c.customCosts)) {
                c.customCosts.forEach(cc => {
                    if (cc.name && Number(cc.amount) > 0) {
                        customMap[cc.name] = (customMap[cc.name] || 0) + Number(cc.amount);
                    }
                });
            }
        });
        const result = [
            { name: '인건비', value: labor },
            { name: '재료비', value: material },
            { name: '임대료', value: rent },
            { name: '장비/감가상각', value: equipment },
            { name: '기타경비', value: other },
            ...Object.entries(customMap).map(([name, value]) => ({ name, value })),
        ].filter(d => d.value > 0);
        return result;
    }, [costsData]);

    // 진단 메시지
    const diagnoses = useMemo(() => {
        const results = [];
        if (totalRevenue <= 0 && totalCost <= 0) {
            results.push({ type: 'info', title: '데이터 부족', message: '매출 및 비용 데이터를 입력하면 경영 진단을 받을 수 있습니다.', action: '비용 관리', actionTab: 'cost' });
            return results;
        }

        if (operatingProfitRate < 0) {
            results.push({ type: 'danger', title: '영업 적자 발생', message: `현재 영업이익률이 ${operatingProfitRate.toFixed(1)}%로 적자 상태입니다. 비용 절감 또는 매출 증대 방안을 검토해 보세요.`, suggestion: '비용 관리 탭에서 각 항목별 지출을 점검하고, 절감 가능한 비용을 파악해 보세요.', action: '비용 분석 보기', actionTab: 'cost' });
        } else if (operatingProfitRate < 10) {
            results.push({ type: 'warning', title: '낮은 영업이익률', message: `영업이익률이 ${operatingProfitRate.toFixed(1)}%로 업계 평균(15~20%)보다 낮습니다. 원가 구조 개선을 고려해 보세요.`, suggestion: '수익성 탭에서 매출 대비 비용 비율을 확인해 보세요.', action: '수익성 분석 보기', actionTab: 'profit' });
        } else if (operatingProfitRate >= 20) {
            results.push({ type: 'success', title: '우수한 수익성', message: `영업이익률 ${operatingProfitRate.toFixed(1)}%로 양호한 수익 구조를 유지하고 있습니다.`, suggestion: '현재 수익 구조를 유지하면서 성장 기회를 탐색해 보세요.', action: '수익성 상세 보기', actionTab: 'profit' });
        }

        if (totalCost > 0) {
            let totalMaterial = 0;
            Object.values(costsData).forEach(c => { totalMaterial += Number(c.materialCost) || 0; });
            const materialRatio = (totalMaterial / totalCost) * 100;
            if (materialRatio > 40) {
                results.push({ type: 'warning', title: '재료비 비중 과다', message: `재료비 비중이 ${materialRatio.toFixed(1)}%입니다. 공급처 다변화나 대량 구매 할인을 검토해 보세요.`, suggestion: '비용 관리 탭에서 월별 재료비 추이를 확인하고 급증 시점을 파악해 보세요.', action: '비용 상세 보기', actionTab: 'cost' });
            }

            let totalLabor = 0;
            Object.values(costsData).forEach(c => { totalLabor += Number(c.laborCost) || 0; });
            const laborRatio = (totalLabor / totalCost) * 100;
            if (laborRatio > 50) {
                results.push({ type: 'warning', title: '인건비 비중 과다', message: `인건비가 전체 비용의 ${laborRatio.toFixed(1)}%를 차지합니다. 업무 효율화 방안을 검토해 보세요.`, suggestion: '외주 활용이나 업무 프로세스 개선으로 인건비 효율을 높일 수 있습니다.', action: '비용 상세 보기', actionTab: 'cost' });
            }
        }

        if (hasGrowthData) {
            if (growthRate < -10) {
                results.push({ type: 'danger', title: '매출 급감', message: `전월 대비 매출이 ${growthRate.toFixed(1)}% 감소했습니다. 거래처 관리와 신규 영업에 집중이 필요합니다.`, suggestion: '매출 현황 탭에서 거래처별 매출 비중을 확인하고 감소 원인을 파악해 보세요.', action: '매출 분석 보기', actionTab: 'revenue' });
            } else if (growthRate > 20) {
                results.push({ type: 'success', title: '높은 매출 성장', message: `전월 대비 ${growthRate.toFixed(1)}% 성장했습니다. 현재 성장 추세를 유지해 보세요.`, suggestion: '성장세를 유지하되, 비용 증가율도 함께 모니터링하세요.', action: '매출 추이 보기', actionTab: 'revenue' });
            }
        }

        if (Object.keys(partnerRevenue).length > 0 && totalRevenue > 0) {
            const topVal = Math.max(...Object.values(partnerRevenue));
            const concentration = (topVal / totalRevenue) * 100;
            if (concentration > 50) {
                results.push({ type: 'warning', title: '거래처 집중도 높음', message: `상위 1개 거래처에 매출의 ${concentration.toFixed(1)}%가 집중되어 있습니다. 거래처 다변화를 고려해 보세요.`, suggestion: '매출 현황의 거래처별 파이 차트에서 매출 분포를 확인해 보세요.', action: '거래처 분석 보기', actionTab: 'revenue' });
            }
        }

        if (results.length === 0) {
            results.push({ type: 'info', title: '경영 상태 양호', message: '현재 특별한 경영 이슈가 감지되지 않았습니다. 지속적인 모니터링을 권장합니다.' });
        }

        return results;
    }, [totalRevenue, totalCost, operatingProfitRate, growthRate, hasGrowthData, partnerRevenue, costsData]);

    // 월 선택 옵션 생성
    const monthOptions = useMemo(() => {
        const now = new Date();
        const options = [];
        for (let i = rangeMonths - 1; i >= 0; i--) {
            const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
            const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
            options.push({ value: val, label: `${d.getFullYear()}년 ${d.getMonth() + 1}월` });
        }
        return options;
    }, [rangeMonths]);

    if (loading) {
        return (
            <div style={styles.container}>
                <PageHeader icon={BarChart3} title="경영분석" />
                <div style={styles.loading}>로딩 중...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div style={styles.container}>
                <PageHeader icon={BarChart3} title="경영분석" />
                <div style={{ textAlign: 'center', padding: 24 }}>
                    <AlertTriangle size={48} color="#ef4444" style={{ marginBottom: 12 }} />
                    <div style={{ color: '#ef4444', fontSize: 15, fontWeight: 600, marginBottom: 16 }}>{error}</div>
                    <button onClick={loadAllData} style={{ padding: '10px 24px', backgroundColor: '#6366f1', color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                        다시 시도
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <style>{`
                .ba-kpi-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-bottom: 14px; }
                .ba-chart-row { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
                .ba-revenue-input { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; }
                @media (max-width: 768px) {
                    .ba-kpi-grid { grid-template-columns: repeat(2, 1fr); }
                    .ba-chart-row { grid-template-columns: 1fr; }
                    .ba-revenue-input { flex-direction: column; align-items: stretch; }
                }
            `}</style>
            <PageHeader icon={BarChart3} title="경영분석" />

            {/* 분석 도구 탭 — 메뉴 개편으로 통계·리메이크분석·매칭수수료를 여기서 진입 (2026-07-19) */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 16, borderBottom: '2px solid #E2E8F0', flexWrap: 'wrap' }}>
                {[
                    ['경영분석', null],
                    ['통계', '/stats'],
                    ['리메이크분석', '/remake-analysis'],
                    ['매칭 수수료', '/commissions'],
                ].map(([label, path]) => (
                    <button
                        key={label}
                        onClick={() => path && navigate(path)}
                        style={{
                            padding: '9px 16px', fontSize: 13.5, fontWeight: 700, border: 'none', background: 'none',
                            cursor: path ? 'pointer' : 'default',
                            color: path ? '#64748B' : '#6366F1',
                            borderBottom: `2.5px solid ${path ? 'transparent' : '#6366F1'}`,
                            marginBottom: -2,
                        }}
                    >
                        {label}
                    </button>
                ))}
            </div>

            {/* Toast 알림 */}
            {toast && (
                <div style={{
                    position: 'fixed', top: 24, right: 24, zIndex: 9999,
                    padding: '14px 24px', borderRadius: 12,
                    backgroundColor: toast.type === 'error' ? '#fef2f2' : '#f0fdf4',
                    color: toast.type === 'error' ? '#dc2626' : '#16a34a',
                    border: `1px solid ${toast.type === 'error' ? '#fecaca' : '#bbf7d0'}`,
                    fontSize: 14, fontWeight: 600, boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    animation: 'slideIn 0.3s ease',
                }}>
                    {toast.type === 'error' ? '⚠ ' : '✓ '}{toast.message}
                </div>
            )}

            {/* 탭 바 + 기간 선택 */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 10 }}>
                <div style={styles.tabBar}>
                    {tabs.map(t => (
                        <button
                            key={t.key}
                            onClick={() => setActiveTab(t.key)}
                            style={{
                                ...styles.tab,
                                backgroundColor: activeTab === t.key ? '#6366f1' : '#f1f5f9',
                                color: activeTab === t.key ? '#fff' : '#64748b',
                            }}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>
                <div style={{ display: 'flex', gap: 4 }}>
                    {[3, 6, 12].map(m => (
                        <button
                            key={m}
                            onClick={() => setRangeMonths(m)}
                            style={{
                                padding: '6px 12px', border: 'none', borderRadius: 8,
                                fontSize: 12, fontWeight: 600, cursor: 'pointer',
                                backgroundColor: rangeMonths === m ? '#0f172a' : '#f1f5f9',
                                color: rangeMonths === m ? '#fff' : '#64748b',
                                transition: 'all 0.2s',
                            }}
                        >
                            {m}개월
                        </button>
                    ))}
                </div>
            </div>

            {/* 탭 컨텐츠 */}
            <div style={styles.content}>
                {activeTab === 'revenue' && renderRevenueTab()}
                {activeTab === 'cost' && renderCostTab()}
                {activeTab === 'profit' && renderProfitTab()}
                {activeTab === 'diagnosis' && renderDiagnosisTab()}
            </div>

            {/* 면책 문구 */}
            <div style={styles.disclaimer}>
                본 분석은 참고용이며, 정확한 재무·회계 판단은 전문가에게 문의하세요.
            </div>
        </div>
    );

    // ─── 매출 현황 탭 ───

    function renderRevenueTab() {
        return (
            <>
                {/* 추가 매출 입력 */}
                <div style={{ ...styles.formCard, border: '1px solid #93c5fd', marginBottom: 20 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                        <div style={{ width: 32, height: 32, borderRadius: 8, backgroundColor: '#dbeafe', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <TrendingUp size={16} color="#3b82f6" />
                        </div>
                        <span style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>추가 매출 입력</span>
                        <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 'auto' }}>거래명세서 외</span>
                    </div>
                    <div className="ba-revenue-input">
                        <select
                            value={selectedRevenueMonth}
                            onChange={e => handleRevenueMonthChange(e.target.value)}
                            style={{ ...styles.select, width: 150, flex: 'none' }}
                        >
                            {monthOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                        <div style={{ ...styles.inputWrapper, flex: 1 }}>
                            <input
                                type="number"
                                min="0"
                                max="999999999999"
                                value={manualRevenueInput}
                                onChange={e => {
                                    const v = e.target.value;
                                    if (v === '' || (/^\d+$/.test(v) && Number(v) >= 0)) setManualRevenueInput(v);
                                }}
                                onKeyDown={e => { if (['-', 'e', 'E', '+', '.'].includes(e.key)) e.preventDefault(); }}
                                placeholder="추가 매출액"
                                style={styles.input}
                            />
                            <span style={styles.inputSuffix}>원</span>
                        </div>
                        <button
                            onClick={handleSaveManualRevenue}
                            disabled={savingRevenue}
                            style={{ padding: '10px 20px', backgroundColor: '#3b82f6', color: '#fff', border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}
                        >
                            {savingRevenue ? '저장 중...' : '저장'}
                        </button>
                    </div>
                </div>

                {/* KPI 카드 */}
                <div className="ba-kpi-grid">
                    <KpiCard title="총 매출" value={`${formatAmount(totalRevenue)}원`} icon={<DollarSign size={20} />} color="#6366f1" />
                    <KpiCard title="거래 건수" value={`${totalOrderCount}건`} icon={<Package size={20} />} color="#3b82f6" />
                    <KpiCard title="평균 단가" value={`${formatAmount(Math.round(averageUnitPrice))}원`} icon={<DollarSign size={20} />} color="#10b981" />
                    <KpiCard title="전월 대비" value={hasGrowthData ? `${growthRate >= 0 ? '+' : ''}${growthRate.toFixed(1)}%` : '집계중'} icon={hasGrowthData ? (growthRate >= 0 ? <TrendingUp size={20} /> : <TrendingDown size={20} />) : <Clock size={20} />} color={hasGrowthData ? (growthRate >= 0 ? '#10b981' : '#ef4444') : '#9ca3af'} />
                </div>

                {/* 월별 매출 추이 (주문/외주/추가 구분) */}
                <div style={styles.chartSection}>
                    <h3 style={styles.sectionTitle}>월별 매출 추이</h3>
                    {revenueChartData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={200}>
                            <BarChart data={revenueChartData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="label" />
                                <YAxis tickFormatter={formatAmount} />
                                <Tooltip formatter={(v) => `${v.toLocaleString()}원`} />
                                <Legend />
                                <Bar dataKey="주문매출" stackId="rev" fill="#6366f1" />
                                <Bar dataKey="외주매출" stackId="rev" fill="#10b981" />
                                <Bar dataKey="추가매출" stackId="rev" fill="#3b82f6" />
                            </BarChart>
                        </ResponsiveContainer>
                    ) : <div style={styles.empty}>
                        <div style={{ fontSize: 20, marginBottom: 8 }}>📊</div>
                        <div style={{ fontWeight: 600, color: '#64748b', marginBottom: 4 }}>매출 데이터가 없습니다</div>
                        <div style={{ fontSize: 12, color: '#94a3b8' }}>거래명세서를 발행하면 자동으로 매출이 집계됩니다</div>
                    </div>}
                </div>

                {/* 거래처별 매출 비중 */}
                <div style={styles.chartSection}>
                    <h3 style={styles.sectionTitle}>거래처별 매출 비중</h3>
                    {partnerPieData.length > 0 ? (
                        <div style={styles.pieWrapper}>
                            <ResponsiveContainer width="100%" height={200}>
                                <PieChart>
                                    <Pie data={partnerPieData} cx="50%" cy="50%" labelLine={false}
                                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                        outerRadius={100} fill="#8884d8" dataKey="value">
                                        {partnerPieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                                    </Pie>
                                    <Tooltip formatter={(v) => `${v.toLocaleString()}원`} />
                                </PieChart>
                            </ResponsiveContainer>
                            <div style={styles.legendList}>
                                {partnerPieData.map((d, i) => (
                                    <div key={d.name} style={styles.legendItem}>
                                        <span style={{ ...styles.legendDot, backgroundColor: COLORS[i % COLORS.length] }} />
                                        <span style={styles.legendName}>{d.name}</span>
                                        <span style={styles.legendValue}>{formatAmount(d.value)}원</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : <div style={styles.empty}>
                        <div style={{ fontSize: 20, marginBottom: 8 }}>🏢</div>
                        <div style={{ fontWeight: 600, color: '#64748b', marginBottom: 4 }}>거래처 데이터가 없습니다</div>
                        <div style={{ fontSize: 12, color: '#94a3b8' }}>거래처와 거래가 시작되면 매출 비중이 표시됩니다</div>
                    </div>}
                </div>
            </>
        );
    }

    // ─── 비용 관리 탭 ───

    function renderCostTab() {
        return (
            <>
                {/* 비용 입력 폼 */}
                <div style={styles.chartSection}>
                    <h3 style={styles.sectionTitle}>월별 비용 입력</h3>
                    <div style={styles.formCard}>
                        <div style={styles.formRow}>
                            <label style={styles.formLabel}>대상 월</label>
                            <select value={selectedMonth} onChange={e => handleMonthChange(e.target.value)} style={styles.select}>
                                {monthOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                        </div>
                        {[
                            { key: 'laborCost', label: '인건비' },
                            { key: 'materialCost', label: '재료비' },
                            { key: 'rentCost', label: '임대료' },
                            { key: 'equipmentCost', label: '장비/감가상각' },
                            { key: 'otherCost', label: '기타경비' },
                        ].map(f => (
                            <div key={f.key} style={styles.formRow}>
                                <label style={styles.formLabel}>{f.label}</label>
                                <div style={styles.inputWrapper}>
                                    <input
                                        type="number"
                                        min="0"
                                        max="999999999999"
                                        value={costForm[f.key]}
                                        onChange={e => {
                                            const v = e.target.value;
                                            if (v === '' || (/^\d+$/.test(v) && Number(v) >= 0)) setCostForm(prev => ({ ...prev, [f.key]: v }));
                                        }}
                                        onKeyDown={e => { if (['-', 'e', 'E', '+', '.'].includes(e.key)) e.preventDefault(); }}
                                        placeholder="0"
                                        style={styles.input}
                                    />
                                    <span style={styles.inputSuffix}>원</span>
                                </div>
                            </div>
                        ))}

                        {/* 커스텀 비용 항목 */}
                        {(costForm.customCosts || []).map((cc, idx) => (
                            <div key={cc.id || idx} style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'center' }}>
                                <input
                                    type="text"
                                    value={cc.name || ''}
                                    onChange={e => {
                                        const updated = [...costForm.customCosts];
                                        updated[idx] = { ...updated[idx], name: e.target.value };
                                        setCostForm(prev => ({ ...prev, customCosts: updated }));
                                    }}
                                    placeholder="항목명"
                                    style={{ ...styles.input, flex: 1 }}
                                />
                                <input
                                    type="number"
                                    min="0"
                                    max="999999999999"
                                    value={cc.amount || ''}
                                    onChange={e => {
                                        const v = e.target.value;
                                        if (v === '' || (/^\d+$/.test(v) && Number(v) >= 0)) {
                                            const updated = [...costForm.customCosts];
                                            updated[idx] = { ...updated[idx], amount: v };
                                            setCostForm(prev => ({ ...prev, customCosts: updated }));
                                        }
                                    }}
                                    onKeyDown={e => { if (['-', 'e', 'E', '+', '.'].includes(e.key)) e.preventDefault(); }}
                                    placeholder="금액"
                                    style={{ ...styles.input, flex: 1 }}
                                />
                                <span style={styles.inputSuffix}>원</span>
                                <button
                                    onClick={() => {
                                        const updated = costForm.customCosts.filter((_, i) => i !== idx);
                                        setCostForm(prev => ({ ...prev, customCosts: updated }));
                                    }}
                                    style={{ padding: '6px 10px', backgroundColor: '#fef2f2', color: '#ef4444', border: '1px solid #fecaca', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
                                >
                                    삭제
                                </button>
                            </div>
                        ))}
                        <button
                            onClick={() => setCostForm(prev => ({ ...prev, customCosts: [...(prev.customCosts || []), { id: `${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, name: '', amount: '' }] }))}
                            style={{ width: '100%', padding: '10px 0', backgroundColor: '#f8fafc', color: '#6366f1', border: '1px dashed #c7d2fe', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer', marginBottom: 8 }}
                        >
                            + 비용 항목 추가
                        </button>

                        <button onClick={handleSaveCost} disabled={saving} style={styles.saveBtn}>
                            {saving ? '저장 중...' : '저장'}
                        </button>
                    </div>
                </div>

                {/* 비용 추이 */}
                <div style={styles.chartSection}>
                    <h3 style={styles.sectionTitle}>비용 추이</h3>
                    {costChartData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={200}>
                            <BarChart data={costChartData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="label" />
                                <YAxis tickFormatter={formatAmount} />
                                <Tooltip formatter={(v) => `${v.toLocaleString()}원`} />
                                <Legend />
                                <Bar dataKey="인건비" stackId="a" fill="#6366f1" />
                                <Bar dataKey="재료비" stackId="a" fill="#10b981" />
                                <Bar dataKey="임대료" stackId="a" fill="#f59e0b" />
                                <Bar dataKey="장비/감가상각" stackId="a" fill="#3b82f6" />
                                <Bar dataKey="기타경비" stackId="a" fill="#ef4444" />
                            </BarChart>
                        </ResponsiveContainer>
                    ) : <div style={styles.empty}>
                        <div style={{ fontSize: 20, marginBottom: 8 }}>💰</div>
                        <div style={{ fontWeight: 600, color: '#64748b', marginBottom: 4 }}>비용 데이터가 없습니다</div>
                        <div style={{ fontSize: 12, color: '#94a3b8' }}>위 폼에서 월별 비용을 입력하면 추이가 표시됩니다</div>
                    </div>}
                </div>

                {/* 비용 항목별 비중 */}
                <div style={styles.chartSection}>
                    <h3 style={styles.sectionTitle}>비용 항목별 비중</h3>
                    {costPieData.length > 0 ? (
                        <div style={styles.pieWrapper}>
                            <ResponsiveContainer width="100%" height={200}>
                                <PieChart>
                                    <Pie data={costPieData} cx="50%" cy="50%" labelLine={false}
                                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                        outerRadius={100} dataKey="value">
                                        {costPieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                                    </Pie>
                                    <Tooltip formatter={(v) => `${v.toLocaleString()}원`} />
                                </PieChart>
                            </ResponsiveContainer>
                            <div style={styles.legendList}>
                                {costPieData.map((d, i) => (
                                    <div key={d.name} style={styles.legendItem}>
                                        <span style={{ ...styles.legendDot, backgroundColor: COLORS[i % COLORS.length] }} />
                                        <span style={styles.legendName}>{d.name}</span>
                                        <span style={styles.legendValue}>{formatAmount(d.value)}원</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : <div style={styles.empty}>
                        <div style={{ fontSize: 20, marginBottom: 8 }}>💰</div>
                        <div style={{ fontWeight: 600, color: '#64748b', marginBottom: 4 }}>비용 데이터가 없습니다</div>
                        <div style={{ fontSize: 12, color: '#94a3b8' }}>위 폼에서 월별 비용을 입력하면 추이가 표시됩니다</div>
                    </div>}
                </div>
            </>
        );
    }

    // ─── 수익성 분석 탭 ───

    function renderProfitTab() {
        return (
            <>
                {/* 수익성 KPI */}
                <div className="ba-kpi-grid">
                    <KpiCard title="영업이익" value={`${formatAmount(Math.round(operatingProfit))}원`} icon={<DollarSign size={20} />} color={operatingProfit >= 0 ? '#10b981' : '#ef4444'} />
                    <KpiCard title="영업이익률" value={`${operatingProfitRate.toFixed(1)}%`} icon={<Percent size={20} />} color={operatingProfitRate >= 15 ? '#10b981' : operatingProfitRate >= 0 ? '#f59e0b' : '#ef4444'} />
                    <KpiCard title="총 매출" value={`${formatAmount(totalRevenue)}원`} icon={<TrendingUp size={20} />} color="#6366f1" />
                    <KpiCard title="총 비용" value={`${formatAmount(totalCost)}원`} icon={<TrendingDown size={20} />} color="#f59e0b" />
                </div>

                {/* 매출-비용 대비 */}
                <div style={styles.chartSection}>
                    <h3 style={styles.sectionTitle}>매출-비용 대비</h3>
                    {monthlyProfitData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={200}>
                            <BarChart data={monthlyProfitData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="label" />
                                <YAxis tickFormatter={formatAmount} />
                                <Tooltip formatter={(v) => `${v.toLocaleString()}원`} />
                                <Legend />
                                <Bar dataKey="revenue" fill="#6366f1" name="매출" />
                                <Bar dataKey="cost" fill="#f59e0b" name="비용" />
                            </BarChart>
                        </ResponsiveContainer>
                    ) : <div style={styles.empty}>
                        <div style={{ fontSize: 20, marginBottom: 8 }}>📈</div>
                        <div style={{ fontWeight: 600, color: '#64748b', marginBottom: 4 }}>데이터가 없습니다</div>
                        <div style={{ fontSize: 12, color: '#94a3b8' }}>매출과 비용 데이터가 입력되면 분석이 표시됩니다</div>
                    </div>}
                </div>

                {/* 월별 영업이익률 */}
                <div style={styles.chartSection}>
                    <h3 style={styles.sectionTitle}>월별 영업이익률</h3>
                    {monthlyProfitData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={200}>
                            <LineChart data={monthlyProfitData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="label" />
                                <YAxis tickFormatter={v => `${v}%`} />
                                <Tooltip formatter={(v) => [`${v}%`, '영업이익률']} />
                                <Line type="monotone" dataKey="rate" stroke="#10b981" strokeWidth={3} name="영업이익률"
                                    dot={{ r: 5, fill: '#10b981', strokeWidth: 2, stroke: '#fff' }} />
                                {/* 0% 기준선 */}
                                <Line type="monotone" dataKey={() => 0} stroke="#ef4444" strokeDasharray="5 5" strokeWidth={1} dot={false} />
                            </LineChart>
                        </ResponsiveContainer>
                    ) : <div style={styles.empty}>
                        <div style={{ fontSize: 20, marginBottom: 8 }}>📈</div>
                        <div style={{ fontWeight: 600, color: '#64748b', marginBottom: 4 }}>데이터가 없습니다</div>
                        <div style={{ fontSize: 12, color: '#94a3b8' }}>매출과 비용 데이터가 입력되면 분석이 표시됩니다</div>
                    </div>}
                </div>
            </>
        );
    }

    // ─── 경영 진단 탭 ───

    function renderDiagnosisTab() {
        return (
            <>
                <h3 style={styles.sectionTitle}>경영 진단 리포트</h3>
                {diagnoses.map((d, i) => (
                    <DiagnosisCard key={i} diagnosis={d} onTabChange={setActiveTab} />
                ))}
            </>
        );
    }
}

// ─── 하위 컴포넌트 ───

function KpiCard({ title, value, icon, color }) {
    return (
        <div style={styles.kpiCard}>
            <div style={{ ...styles.kpiIcon, backgroundColor: `${color}15` }}>
                <span style={{ color }}>{icon}</span>
            </div>
            <div style={styles.kpiLabel}>{title}</div>
            <div style={{ ...styles.kpiValue, color }}>{value}</div>
        </div>
    );
}

function DiagnosisCard({ diagnosis, onTabChange }) {
    const typeConfig = {
        danger: { color: '#ef4444', bg: '#fef2f2', icon: <AlertTriangle size={22} /> },
        warning: { color: '#f59e0b', bg: '#fef3c7', icon: <Info size={22} /> },
        success: { color: '#10b981', bg: '#d1fae5', icon: <CheckCircle size={22} /> },
        info: { color: '#3b82f6', bg: '#dbeafe', icon: <Lightbulb size={22} /> },
    };
    const cfg = typeConfig[diagnosis.type] || typeConfig.info;

    return (
        <div style={{ ...styles.diagCard, backgroundColor: cfg.bg, borderColor: `${cfg.color}30` }}>
            <div style={{ ...styles.diagIcon, backgroundColor: `${cfg.color}15` }}>
                <span style={{ color: cfg.color }}>{cfg.icon}</span>
            </div>
            <div style={styles.diagContent}>
                <div style={{ ...styles.diagTitle, color: cfg.color }}>{diagnosis.title}</div>
                <div style={styles.diagMessage}>{diagnosis.message}</div>
                {diagnosis.suggestion && (
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 6, lineHeight: 1.5 }}>
                        💡 {diagnosis.suggestion}
                    </div>
                )}
                {diagnosis.action && onTabChange && (
                    <button
                        onClick={() => onTabChange(diagnosis.actionTab)}
                        style={{
                            marginTop: 10, padding: '6px 14px', border: 'none', borderRadius: 8,
                            backgroundColor: `${cfg.color}15`, color: cfg.color,
                            fontSize: 12, fontWeight: 700, cursor: 'pointer',
                        }}
                    >
                        {diagnosis.action} →
                    </button>
                )}
            </div>
        </div>
    );
}

const styles = {
    container: { padding: '16px', maxWidth: 1200, margin: '0 auto' },
    loading: { textAlign: 'center', padding: 24, color: '#94a3b8' },
    tabBar: { display: 'flex', gap: 8, overflowX: 'auto', WebkitOverflowScrolling: 'touch' },
    tab: {
        padding: '8px 16px', border: 'none', borderRadius: 8,
        fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s',
        whiteSpace: 'nowrap', flexShrink: 0,
    },
    content: {},
    disclaimer: {
        textAlign: 'center', fontSize: 11, color: '#94a3b8',
        padding: '12px 0 10px', borderTop: '1px solid #e2e8f0', marginTop: 16,
    },

    // KPI
    kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 14 },
    kpiCard: {
        padding: 10, backgroundColor: '#fff', border: '1px solid #e2e8f0',
        borderRadius: 14, textAlign: 'left',
    },
    kpiIcon: {
        width: 28, height: 28, borderRadius: 10,
        display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 6,
    },
    kpiLabel: { fontSize: 11, color: '#94a3b8', marginBottom: 2 },
    kpiValue: { fontSize: 15, fontWeight: 700 },

    // Chart sections
    chartSection: { marginBottom: 14 },
    sectionTitle: { fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 8 },
    empty: {
        padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: 13,
        backgroundColor: '#fff', borderRadius: 14, border: '1px solid #e2e8f0',
    },

    // Pie wrapper
    pieWrapper: { backgroundColor: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', padding: 16 },
    legendList: { marginTop: 10 },
    legendItem: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 },
    legendDot: { width: 12, height: 12, borderRadius: 3, flexShrink: 0 },
    legendName: { flex: 1, fontSize: 13, color: '#64748b' },
    legendValue: { fontSize: 13, fontWeight: 600, color: '#0f172a' },

    // Cost form
    formCard: { backgroundColor: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', padding: 14 },
    formRow: { marginBottom: 8 },
    formLabel: { display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 4 },
    select: {
        width: '100%', padding: '8px 10px', borderRadius: 10, border: '1px solid #e2e8f0',
        fontSize: 14, color: '#0f172a', backgroundColor: '#fff',
    },
    inputWrapper: { display: 'flex', alignItems: 'center', gap: 6 },
    input: {
        flex: 1, padding: '8px 10px', borderRadius: 10, border: '1px solid #e2e8f0',
        fontSize: 14, color: '#0f172a',
    },
    inputSuffix: { fontSize: 13, color: '#94a3b8', flexShrink: 0 },
    saveBtn: {
        width: '100%', padding: '8px 0', backgroundColor: '#6366f1', color: '#fff',
        border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 600,
        cursor: 'pointer', marginTop: 8,
    },

    // Diagnosis
    diagCard: {
        display: 'flex', gap: 14, padding: 12, borderRadius: 14,
        border: '1px solid', marginBottom: 8,
    },
    diagIcon: {
        width: 32, height: 32, borderRadius: 12, flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
    },
    diagContent: { flex: 1 },
    diagTitle: { fontSize: 14, fontWeight: 700, marginBottom: 4 },
    diagMessage: { fontSize: 13, color: '#334155', lineHeight: 1.6 },
};

export default BusinessAnalysis;
